import React from "react";
import { Check, X } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";

const GAPS = [
  {
    title: "The mind–body split",
    body:
      "Most behavior-change models treat cognition and physiology as separate systems — as if the nervous system will simply follow the intentions the mind sets for it. It rarely does.",
  },
  {
    title: "Willpower fatigue",
    body:
      "Willpower is a finite, physiologically-taxing resource. Approaches that depend on it are structurally fragile — and they most often fail at moments of dysregulation, exactly when they are needed most.",
  },
  {
    title: "Symptom management instead of state change",
    body:
      "Medication, coping strategies, and behavioral suppression can quiet symptoms without shifting the underlying physiology producing them.",
  },
  {
    title: "Exercise treated as fitness, not as signal",
    body:
      "The most direct input we have into the nervous system is treated as a wellness accessory. Adaptive Physiology treats it as the central intervention.",
  },
];

const COMPARE = [
  {
    dimension: "Primary target",
    traditional: "Thoughts, symptoms, motivation",
    adaptive: "Physiological state driving behavior",
  },
  {
    dimension: "Change mechanism",
    traditional: "Insight, discipline, medication",
    adaptive: "Exercise physiology, behavioral conditioning, environmental adaptation",
  },
  {
    dimension: "Measurement",
    traditional: "Self-report, symptom scales",
    adaptive: "HRV, autonomic tone, sleep, engagement, capacity markers",
  },
  {
    dimension: "Feedback loop",
    traditional: "Weekly or monthly sessions",
    adaptive: "Closed-loop: assess → prescribe → reinforce → reassess",
  },
  {
    dimension: "Definition of progress",
    traditional: "Reduced symptoms",
    adaptive: "A retrained set-point that produces different behavior by default",
  },
];

const Why = () => {
  return (
    <PageShell>
      <SubHero
        testId="why-hero"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Methodology", to: "/methodology" },
          { label: "Why Adaptive Physiology?" },
        ]}
        overline="Why Adaptive Physiology?"
        headline="Behavior is not the problem."
        headlineAccent="The physiological state producing it is."
        lead="Traditional models of behavior change target the wrong layer of the system. Adaptive Physiology is a response to that gap — a methodology built to intervene where behavior actually originates."
      />

      <section
        data-testid="why-gap-section"
        className="relative py-24 sm:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <Reveal>
                <SectionOverline
                  number="01"
                  label="The Gap in Behavior Change"
                  testId="why-gap-overline"
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Four reasons most approaches fall short.
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-8 lg:pl-8">
              <div className="space-y-6">
                {GAPS.map((g, i) => (
                  <Reveal delay={i * 0.06} key={g.title}>
                    <div
                      data-testid={`why-gap-${i + 1}`}
                      className="forr-card rounded-[24px] p-8"
                    >
                      <div className="flex items-baseline gap-6">
                        <span className="font-display forr-text-teal-deep text-lg font-medium">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <h3 className="font-display forr-text-ink text-xl font-medium tracking-tight">
                            {g.title}
                          </h3>
                          <p className="mt-3 text-sm font-light forr-text-soft leading-relaxed">
                            {g.body}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="why-difference-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="02"
              label="What's Different"
              testId="why-difference-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              A methodology built around physiology first.
            </h2>
          </Reveal>

          <div className="mt-16 rounded-[24px] border border overflow-hidden">
            <div className="grid grid-cols-12 forr-surface-tint px-6 sm:px-10 py-5 text-[11px] uppercase tracking-[0.24em] forr-text-mute">
              <div className="col-span-12 md:col-span-4">Dimension</div>
              <div className="hidden md:block md:col-span-4">Traditional approaches</div>
              <div className="hidden md:block md:col-span-4 forr-text-teal-deep">
                Adaptive Physiology™
              </div>
            </div>
            {COMPARE.map((c, i) => (
              <div
                key={c.dimension}
                data-testid={`why-compare-row-${i + 1}`}
                className="grid grid-cols-12 px-6 sm:px-10 py-6 border-t border text-sm"
              >
                <div className="col-span-12 md:col-span-4 font-display forr-text-ink font-medium">
                  {c.dimension}
                </div>
                <div className="col-span-12 md:col-span-4 mt-2 md:mt-0 flex items-start gap-3 forr-text-soft font-light leading-relaxed">
                  <X
                    size={14}
                    strokeWidth={1.75}
                    className="forr-text-mute mt-1 shrink-0"
                  />
                  <span>{c.traditional}</span>
                </div>
                <div className="col-span-12 md:col-span-4 mt-2 md:mt-0 flex items-start gap-3 forr-text-ink font-light leading-relaxed">
                  <Check
                    size={14}
                    strokeWidth={1.75}
                    className="forr-text-teal-deep mt-1 shrink-0"
                  />
                  <span>{c.adaptive}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="why-now-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="03"
                  label="Why Now"
                  testId="why-now-overline"
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  The environment has changed. The nervous system adapted.
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <Reveal delay={0.1}>
                <p className="text-lg font-light forr-text-soft leading-relaxed">
                  Constant novelty. Algorithmic reinforcement. Compressed sleep.
                  Chronic low-grade stress. Outsourced dopamine. The physiology of
                  the modern human is being trained continuously — just not toward
                  regulation.
                </p>
                <p className="mt-6 text-lg font-light forr-text-soft leading-relaxed">
                  Adaptive Physiology exists because the nervous system needs
                  deliberate, structured retraining to counter what the environment
                  is already teaching it every day.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <NextStep
        overline="Next"
        title="The Framework"
        description="The four pillars of the Adaptive Physiology™ methodology — from physiological regulation to adaptive performance."
        to="/methodology/framework"
        cta="Explore the framework"
        testId="why-next-step"
      />
    </PageShell>
  );
};

export default Why;
